import classNames from 'classnames';
import React, { useState } from 'react';
import { SuccessIcon, WarningIcon, IconCrossM, IconInfoL } from '../../icons';
import { FormattedMessage } from '../../util/reactIntl';

import css from './Alert.css';
/**
 * @typedef {'warning' | 'info' | 'failure' | 'success'} AlertType
 */

/**
 * @param {Object} props
 * @param {string} [props.rootClassName] - Custom class for the root container
 * @param {string} [props.closeClassName] - Custom class for the close button
 * @param {AlertType} [props.type='warning'] -  (warning, info, failure, success)
 * @param {boolean} [props.closeAllowed=true] - Is it allowed to close an alert
 * @param {() => void} [props.closeCallback] - Callback called on close
 * @param {React.ReactNode} [props.children] - body
 * @param {React.ReactNode} [props.header] - header
 * @param {React.ReactNode} [props.description] - description
 * @param {React.ReactNode} [props.customIcon=null] - Custom Icon
 * @param {boolean} [props.showAsPlainText] - Whether to display content as plain text
 * @param {React.RefObject<HTMLDivElement> | ((node: HTMLDivElement | null) => void)} [props.rootRef] - Ref on root div
 */
const Alert = ({
    rootClassName,
    closeClassName,
    type = 'warning',
    closeAllowed = true,
    closeCallback = () => null,
    children: body,
    header,
    description,
    customIcon = null,
    showAsPlainText,
    rootRef,
}) => {
    const [visible, setVisible] = useState(true);

    const isWarning = type === 'warning';
    const isSuccess = type === 'success';
    const isFailure = type === 'failure';
    const isInfo = type === 'info';
    const refPropMaybe = rootRef ? { ref: rootRef } : {};

    return visible ? (
        <div
            className={classNames(css.root, css[type], {
                [rootClassName]: !!rootClassName,
            })}
            {...refPropMaybe}
        >
            {!customIcon && (isWarning || isFailure) && <WarningIcon />}
            {!customIcon && isSuccess && <SuccessIcon />}
            {!customIcon && isInfo && <IconInfoL />}
            {customIcon}
            <div>
                {header && <h4>{showAsPlainText ? header : <FormattedMessage id={header} />}</h4>}
                {description && typeof description === 'string' ? (
                    <p>{showAsPlainText ? description : <FormattedMessage id={description} />}</p>
                ) : description ? (
                    description
                ) : null}
            </div>
            {body}
            {closeAllowed && (
                <span
                    className={classNames(css.close, {
                        [closeClassName]: !!closeClassName,
                    })}
                    onClick={() => {
                        closeCallback();
                        setVisible(false);
                    }}
                >
                    <IconCrossM viewBox="-1.5 -1.5 14 14" />
                </span>
            )}
        </div>
    ) : null;
};

export default Alert;
